// Фото по желанию в анкете карты «Рядом с домом» — общее для браузера и
// сервера: пределы, проверка по сигнатуре файла, путь в бакете, адрес показа.
//
// Хранение — приватный бакет map-partner-photos (миграция
// 20261015120000_map_partner_photos.sql). В map_partners.photo_url лежит
// ПУТЬ «map-partner-photos/<id>.<ext>», а не ссылка: снаружи бакет не виден,
// фото опубликованной анкеты отдаёт только серверный маршрут
// /api/map/photo/<id> (src/routes/api/map/photo/$id.ts).
//
// Здесь нет ни Supabase, ни сети — файл импортирует и страница, и
// scripts/verify-map-photo.mts.

export const MAP_PHOTO_BUCKET = "map-partner-photos";
/** 3 МБ — тот же предел, что file_size_limit бакета. */
export const MAP_PHOTO_MAX_BYTES = 3 * 1024 * 1024;
/** Сторона итогового квадрата после обрезки в браузере. */
export const MAP_PHOTO_SIDE = 512;
/** base64 длиннее исходника в 4/3 раза; с запасом на выравнивание. */
export const MAP_PHOTO_MAX_B64 = Math.ceil(MAP_PHOTO_MAX_BYTES / 3) * 4 + 4;

export type PhotoExt = "jpg" | "png" | "webp";
export const PHOTO_MIME: Record<PhotoExt, string> = { jpg: "image/jpeg", png: "image/png", webp: "image/webp" };
/** Что принимает поле выбора файла. */
export const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp";
/**
 * Что можно выбрать в форме (26.10.2026): любое изображение, которое откроет
 * браузер, — HEIC с iPhone, AVIF, GIF. Браузер сам ужимает его до 512×512
 * webp/jpeg, на сервер уходит уже это (PHOTO_ACCEPT и 3 МБ — для него).
 */
export const PHOTO_INPUT_ACCEPT = "image/*,.heic,.heif";
/** Исходный файл в форме — до 25 МБ: фото с телефона 4–8 МБ отказывали при лимите 3 МБ. */
export const MAP_PHOTO_INPUT_MAX_BYTES = 25 * 1024 * 1024;

/** Тип по первым байтам файла. Расширению и content-type не верим. */
export function sniffImage(b: Uint8Array): PhotoExt | null {
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "jpg";
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47 && b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a) return "png";
  if (
    b.length >= 12 &&
    b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && // RIFF
    b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50 // WEBP
  )
    return "webp";
  return null;
}

function b64ToBytes(b64: string): Uint8Array | null {
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(b64)) return null;
  try {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

export type PhotoReject = "photo_consent" | "photo_size" | "photo_type";
export type DecodedPhoto = { ok: true; bytes: Uint8Array; ext: PhotoExt; mime: string } | { ok: false; reason: PhotoReject };

/**
 * Серверная проверка фото из анкеты. Порядок важен: без галочки фото не
 * принимается вовсе, даже правильное. Дальше — размер, потом сигнатура.
 * Без фото (undefined / пустая строка) — не ошибка: поле по желанию.
 */
export function decodePhoto(b64: string | undefined | null, consent: boolean | undefined | null): DecodedPhoto | null {
  if (b64 === undefined || b64 === null || b64 === "") return null;
  if (consent !== true) return { ok: false, reason: "photo_consent" };
  if (b64.length > MAP_PHOTO_MAX_B64) return { ok: false, reason: "photo_size" };
  const bytes = b64ToBytes(b64);
  if (!bytes || bytes.length === 0) return { ok: false, reason: "photo_type" };
  if (bytes.length > MAP_PHOTO_MAX_BYTES) return { ok: false, reason: "photo_size" };
  const ext = sniffImage(bytes);
  if (!ext) return { ok: false, reason: "photo_type" };
  return { ok: true, bytes, ext, mime: PHOTO_MIME[ext] };
}

/** Ключ объекта внутри бакета и то, что пишется в photo_url. */
export const photoObjectKey = (id: string, ext: PhotoExt) => `${id}.${ext}`;
export const photoStoredPath = (id: string, ext: PhotoExt) => `${MAP_PHOTO_BUCKET}/${photoObjectKey(id, ext)}`;

/** Из photo_url («map-partner-photos/<id>.<ext>») — ключ в бакете; чужое — null. */
export function objectKeyFromStored(stored: string | null | undefined): string | null {
  if (!stored) return null;
  const prefix = `${MAP_PHOTO_BUCKET}/`;
  if (!stored.startsWith(prefix)) return null;
  const key = stored.slice(prefix.length);
  return /^[0-9a-f-]{36}\.(jpg|png|webp)$/.test(key) ? key : null;
}

export function mimeOfKey(key: string): string {
  const ext = key.split(".").pop() as PhotoExt;
  return PHOTO_MIME[ext] ?? "application/octet-stream";
}

/** Адрес показа фото опубликованной анкеты. ?v= — смена файла сбрасывает кэш. */
export const publicPhotoUrl = (id: string, stored: string) => `/api/map/photo/${id}?v=${encodeURIComponent(stored.split(".").pop() ?? "")}`;

/**
 * Инициалы — когда фото нет (так же, как в предпросмотре анкеты).
 * По кодовым точкам и после NFKC (02.11.2026): имя «𝑁𝑖𝑔𝑎𝑟 Amirova» набрано
 * математическими буквами, w[0] давал половину суррогатной пары — сервер печатал
 * её как «�», браузер иначе, и гидрация /tutors падала (React #418).
 */
export const initialsOf = (name: string) => {
  const words = name.normalize("NFKC").trim().split(/\s+/).filter(Boolean);
  return words.length ? words.map((w) => Array.from(w)[0]).slice(0, 2).join("").toUpperCase() : "?";
};
